import type { ErrorEvent } from '@sentry/nextjs';

/**
 * httpStatus + status 속성을 가진 API 에러 (예: ZepQuizApiError)
 */
interface ApiErrorLike {
  name: string;
  status: string;
  httpStatus: number;
}

function isApiErrorLike(error: unknown): error is ApiErrorLike {
  return (
    error instanceof Error &&
    'httpStatus' in error &&
    typeof error.httpStatus === 'number' &&
    'status' in error &&
    typeof error.status === 'string'
  );
}

/**
 * API 에러를 ApiErrorLike로 narrowing
 * beforeSend에서 한번만 호출하여 이중 타입 체크 방지
 */
export function asApiErrorLike(error: unknown): ApiErrorLike | null {
  return isApiErrorLike(error) ? error : null;
}

/**
 * Sentry에 보내지 않아도 되는 예상 가능한 HTTP 에러 코드
 *
 * 관련: apps/quiz-app/src/api/apiUtil.ts의 isIgnoreError(401, 403)와 유사하나
 * Sentry 필터링 목적으로 더 넓은 범위를 커버
 */
export const NOISE_HTTP_STATUS_CODES = new Set([401, 403, 404, 422]);

/**
 * 임의 에러 객체에서 HTTP status를 best-effort로 추출한다.
 *
 * 동기: `asApiErrorLike`는 `httpStatus + status` 둘 다 가진 annotated 에러만
 * 매칭한다. 그래서 `annotateAxiosError`가 안 씌워진 axios 인스턴스(weegloo CDN
 * 등)에서 던진 native AxiosError나, `response.status`만 있는 ZepQuizApiError가
 * NOISE_HTTP_STATUS_CODES 필터를 통과해 Sentry로 새는 회귀가 있었다
 * (예: PLAY-APP-A8W 14일 2095 events / 465 users, 404).
 *
 * 우선순위:
 *  1. `error.httpStatus` (annotateAxiosError로 가공된 케이스)
 *  2. `error.response.status` (native AxiosError / config 보존된 ZepQuizApiError)
 *
 * 위 두 곳에서 못 찾으면 `null`. 네트워크 에러/타임아웃처럼 응답 자체가 없는
 * 케이스는 의도적으로 제외 — 진짜 시그널일 수 있어 드랍 정책은 별도 결정.
 */
export function extractHttpStatus(error: unknown): number | null {
  if (error == null || typeof error !== 'object') return null;
  const candidate = error as {
    httpStatus?: unknown;
    response?: { status?: unknown };
  };
  if (typeof candidate.httpStatus === 'number') return candidate.httpStatus;
  if (typeof candidate.response?.status === 'number') {
    return candidate.response.status;
  }
  return null;
}

/**
 * API 에러에 커스텀 fingerprint + 태그를 적용
 * - fingerprint: [name, httpStatus, status] → 같은 종류의 에러끼리 그룹핑
 * - tags: api_http_status, api_status → 대시보드 필터링용
 */
export function applyApiErrorFingerprint(
  event: ErrorEvent,
  apiError: ApiErrorLike,
): void {
  const httpStatusStr = String(apiError.httpStatus);

  event.fingerprint = [apiError.name, httpStatusStr, apiError.status];

  if (!event.tags) event.tags = {};
  event.tags['api_http_status'] = httpStatusStr;
  event.tags['api_status'] = apiError.status;
}
